const normalizeCourseNumber = (course) => {
  const value = Number(course?.course_number ?? course?.courseNumber);
  return Number.isInteger(value) && value > 0 ? value : null;
};

const normalizeLifecycleVersion = (course) =>
  Math.max(
    1,
    Number(course?.lifecycle_version ?? course?.lifecycleVersion ?? 1) || 1,
  );

export const normalizeCourseState = (course = {}) => {
  const courseNumber = normalizeCourseNumber(course);
  if (!courseNumber) return null;
  return {
    ...course,
    course_number: courseNumber,
    courseNumber,
    lifecycle_version: normalizeLifecycleVersion(course),
    lifecycleVersion: normalizeLifecycleVersion(course),
    status: String(course?.status || "held").trim().toLowerCase(),
    production_dispatch_status: String(
      course?.production_dispatch_status ??
        course?.productionDispatchStatus ??
        "",
    )
      .trim()
      .toLowerCase(),
  };
};

export const buildCourseStateSignature = (courses = []) =>
  (Array.isArray(courses) ? courses : [])
    .map(normalizeCourseState)
    .filter(Boolean)
    .sort((left, right) => left.course_number - right.course_number)
    .map((course) =>
      [
        course.id ?? "",
        course.order_id ?? course.orderId ?? "",
        course.course_number,
        course.lifecycle_version,
        course.status,
        course.production_dispatch_status,
        course.fired_at ?? course.firedAt ?? "",
        course.completed_at ?? course.completedAt ?? "",
        course.cancelled_at ?? course.cancelledAt ?? "",
      ].join("|"),
    )
    .join(";");

export const replaceAuthoritativeCourseStates = (courses = []) =>
  (Array.isArray(courses) ? courses : [])
    .map(normalizeCourseState)
    .filter(Boolean)
    .sort((left, right) => left.course_number - right.course_number);

export const retainLocalDraftCourseStates = (
  currentCourses = [],
  pendingItems = [],
) => {
  const localHeldCourses = replaceAuthoritativeCourseStates(currentCourses).filter(
    (course) =>
      course.status === "held" &&
      !course.id &&
      !(course.order_id ?? course.orderId),
  );
  const courseNumbers = new Set(localHeldCourses.map((course) => course.course_number));
  (Array.isArray(pendingItems) ? pendingItems : []).forEach((item) => {
    const status = String(item?.status || "").trim().toLowerCase();
    if (["cancelled", "deleted", "removed", "voided"].includes(status)) return;
    const courseNumber = Number(item?.course_number ?? item?.courseNumber ?? 1);
    if (Number.isInteger(courseNumber) && courseNumber > 0) {
      courseNumbers.add(courseNumber);
    }
  });

  return [...courseNumbers]
    .sort((left, right) => left - right)
    .map(
      (courseNumber) =>
        localHeldCourses.find(
          (course) => course.course_number === courseNumber,
        ) || normalizeCourseState({ course_number: courseNumber, status: "held" }),
    );
};

const COURSE_STATUS_RANK = Object.freeze({
  held: 0,
  fired: 1,
  completed: 2,
  cancelled: 2,
});

const DISPATCH_STATUS_RANK = Object.freeze({
  "": 0,
  pending: 0,
  failed: 1,
  sent: 2,
});

const keepMonotonicCourseState = (current, incoming) => {
  if (!current) return incoming;
  if (incoming.lifecycle_version > current.lifecycle_version) return incoming;
  if (incoming.lifecycle_version < current.lifecycle_version) return current;

  const currentStatusRank = COURSE_STATUS_RANK[current.status] ?? 0;
  const incomingStatusRank = COURSE_STATUS_RANK[incoming.status] ?? 0;
  const statusSource =
    incomingStatusRank >= currentStatusRank ? incoming : current;
  const currentDispatchRank =
    DISPATCH_STATUS_RANK[current.production_dispatch_status] ?? 0;
  const incomingDispatchRank =
    DISPATCH_STATUS_RANK[incoming.production_dispatch_status] ?? 0;
  const dispatchSource =
    incomingDispatchRank >= currentDispatchRank ? incoming : current;

  return normalizeCourseState({
    ...current,
    ...incoming,
    status: statusSource.status,
    production_dispatch_status: dispatchSource.production_dispatch_status,
    fired_at: incoming.fired_at || current.fired_at || null,
    completed_at: incoming.completed_at || current.completed_at || null,
    cancelled_at: incoming.cancelled_at || current.cancelled_at || null,
  });
};

export const mergeAuthoritativeCourseStates = (
  currentCourses = [],
  incomingCourses = [],
) => {
  const current = replaceAuthoritativeCourseStates(currentCourses);
  return replaceAuthoritativeCourseStates(incomingCourses).map((incoming) => {
    const matchingCurrent = current.find(
      (course) =>
        (incoming.id && Number(course.id) === Number(incoming.id)) ||
        (course.course_number === incoming.course_number &&
          String(course.order_id ?? course.orderId ?? "") ===
            String(incoming.order_id ?? incoming.orderId ?? "")),
    );
    return keepMonotonicCourseState(matchingCurrent, incoming);
  });
};

export const upsertAuthoritativeCourseState = (courses = [], incoming = {}) => {
  const normalizedIncoming = normalizeCourseState(incoming);
  if (!normalizedIncoming) return replaceAuthoritativeCourseStates(courses);
  const incomingId = Number(normalizedIncoming.id) || null;
  const next = replaceAuthoritativeCourseStates(courses).filter((course) => {
    if (incomingId && Number(course.id) === incomingId) return false;
    return course.course_number !== normalizedIncoming.course_number;
  });
  const matchingCurrent = replaceAuthoritativeCourseStates(courses).find(
    (course) =>
      (incomingId && Number(course.id) === incomingId) ||
      course.course_number === normalizedIncoming.course_number,
  );
  next.push(keepMonotonicCourseState(matchingCurrent, normalizedIncoming));
  return next.sort((left, right) => left.course_number - right.course_number);
};
